/**
 * PrivateChat - Friends Hub Component (Multi-Window Chat System)
 *
 * DATE: 2025-12-17 | Author: IA + Luiz Couto
 * DATE: 2025-12-19 | Refactored to FriendsHub for Multi-Window Chat System
 *
 * This component now serves as a "Friends Hub" - a minimizable floating widget
 * that displays the friends list. Clicking a friend opens a separate
 * ConversationWindow for that conversation.
 *
 * Architecture:
 * - ChatService: Manages WebSocket connection and global state
 * - ChatWindowManager: Manages multiple ConversationWindow instances
 * - PrivateChat (FriendsHub): This component - displays friends list only
 * - ConversationWindow: Individual chat windows for each conversation
 *
 * In mobile mode (viewport < 768px), maintains the original single-panel
 * navigation (list → conversation → back) for better UX on small screens.
 */

import { ChatService, Friend, ConversationSummary } from '../services/ChatService';
import { ChatWindowManager } from '../services/ChatWindowManager';

// ============================================================================
// TYPES (kept for backwards compatibility with mobile mode)
// ============================================================================

interface ChatMessage {
  id?: number;
  fromNetcartasId: number;
  toNetcartasId: number;
  message: string;
  createdAt: number;
  read?: boolean;
  isSystem?: boolean;
}

// ============================================================================
// PRIVATE CHAT (FRIENDS HUB) COMPONENT
// ============================================================================

export class PrivateChat {
  // State
  private isMinimized: boolean = true;
  private friends: Friend[] = [];
  private conversations: ConversationSummary[] = [];

  // Mobile mode state (single-panel navigation)
  private isMobileMode: boolean = false;
  private currentConversation: number | null = null;
  private messages: ChatMessage[] = [];

  // DOM elements
  private container: HTMLElement | null = null;

  // Drag state
  private isDragging: boolean = false;
  private hasDragMoved: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private containerStartX: number = 0;
  private containerStartY: number = 0;

  // Event handlers (bound for cleanup)
  private boundOnConnected: (data: any) => void;
  private boundOnFriendsUpdated: (data: any) => void;
  private boundOnConversationsUpdated: (data: any) => void;
  private boundOnChatReceived: (data: any) => void;
  private boundOnMessagesLoaded: (data: any) => void;
  private boundOnDisconnected: (data: any) => void;

  constructor() {
    console.log(`💬 [2025-12-19] PrivateChat (FriendsHub): Component initialized`);

    // Bind event handlers
    this.boundOnConnected = this.onConnected.bind(this);
    this.boundOnFriendsUpdated = this.onFriendsUpdated.bind(this);
    this.boundOnConversationsUpdated = this.onConversationsUpdated.bind(this);
    this.boundOnChatReceived = this.onChatReceived.bind(this);
    this.boundOnMessagesLoaded = this.onMessagesLoaded.bind(this);
    this.boundOnDisconnected = this.onDisconnected.bind(this);

    // Check mobile mode
    this.isMobileMode = window.innerWidth < 768;
    window.addEventListener('resize', () => {
      this.isMobileMode = window.innerWidth < 768;
    });
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Initialize the chat component
   * Called when user logs in with a valid netcartasId
   */
  public async initialize(netcartasId: number, nickname: string): Promise<void> {
    const chatService = ChatService.getInstance();

    if (chatService.isConnected()) {
      console.log(`💬 [2025-12-19] PrivateChat: Already connected`);
      return;
    }

    console.log(`💬 [2025-12-19] PrivateChat: Initializing for user ${nickname} (${netcartasId})`);

    // Create UI elements
    this.createChatUI();

    // Subscribe to ChatService events
    this.subscribeToEvents();

    // Connect ChatService
    try {
      await chatService.connect(netcartasId, nickname);

      // Setup ChatWindowManager mobile callback
      ChatWindowManager.getInstance().setMobileOpenCallback((friendId, friendNickname, isAI) => {
        this.openConversationMobile(friendId, friendNickname, isAI);
      });

      // Restore windows if not mobile
      if (!this.isMobileMode) {
        await ChatWindowManager.getInstance().restoreState();
      }

    } catch (error) {
      console.error(`❌ [2025-12-19] PrivateChat: Connection failed:`, error);
    }
  }

  /**
   * Open chat with a specific friend
   * Called from profile modal or when receiving a message
   */
  public openChatWith(friendNetcartasId: number, friendNickname: string, isAI: boolean = false): void {
    console.log(`💬 [2025-12-19] PrivateChat: openChatWith called - ${friendNickname} (${friendNetcartasId})`);

    const chatService = ChatService.getInstance();
    if (!chatService.isConnected()) {
      console.error(`❌ [2025-12-19] PrivateChat: Cannot open chat - not connected`);
      return;
    }

    // Ensure hub is expanded
    this.expand();

    // Ensure friend is in the list
    this.ensureFriendExists(friendNetcartasId, friendNickname, isAI);

    if (this.isMobileMode) {
      // Mobile: use single-panel navigation
      this.openConversationMobile(friendNetcartasId, friendNickname, isAI);
    } else {
      // Desktop: open a ConversationWindow
      ChatWindowManager.getInstance().openWindow(friendNetcartasId, friendNickname, isAI);
    }
  }

  /**
   * Disconnect and cleanup
   */
  public disconnect(): void {
    console.log(`💬 [2025-12-19] PrivateChat: Disconnecting`);

    // Unsubscribe from events
    this.unsubscribeFromEvents();

    // Close all windows
    ChatWindowManager.getInstance().closeAllWindows();

    // Disconnect ChatService
    ChatService.getInstance().disconnect();

    // Reset state
    this.friends = [];
    this.conversations = [];
    this.currentConversation = null;
    this.messages = [];
  }

  /**
   * Check if component is initialized
   */
  public isInitialized(): boolean {
    return ChatService.getInstance().isConnected();
  }

  // ============================================================================
  // PRIVATE - UI CREATION
  // ============================================================================

  /**
   * Create the chat UI elements
   */
  private createChatUI(): void {
    // Check if already created
    if (document.getElementById('private-chat-container')) {
      this.container = document.getElementById('private-chat-container');
      return;
    }

    // Create main container
    this.container = document.createElement('div');
    this.container.id = 'private-chat-container';
    this.container.className = 'private-chat-container minimized';

    // Build HTML - includes conversation panel for mobile mode
    this.container.innerHTML = `
      <div class="private-chat-header drag-handle" id="private-chat-header">
        <div class="chat-header-left">
          <span class="private-chat-drag-icon" title="Arrastar">⋮⋮</span>
          <span class="chat-icon">💬</span>
          <span class="chat-title">Chat</span>
          <span class="unread-badge hidden" id="chat-unread-badge">0</span>
        </div>
        <div class="chat-header-right">
          <button class="chat-toggle-btn" id="chat-expand-btn" title="Expandir">▲</button>
        </div>
      </div>

      <div class="private-chat-body hidden" id="private-chat-body">
        <!-- Friends List Panel -->
        <div class="chat-friends-panel" id="chat-friends-panel">
          <div class="friends-header">
            <h4>Amigos</h4>
          </div>
          <div class="friends-list" id="chat-friends-list">
            <div class="empty-friends">Nenhum amigo ainda</div>
          </div>
        </div>

        <!-- Conversation Panel (for mobile mode only) -->
        <div class="chat-conversation-panel hidden" id="chat-conversation-panel">
          <div class="conversation-header" id="conversation-header">
            <button class="back-btn" id="chat-back-btn">←</button>
            <div class="conversation-friend-info">
              <span class="friend-name" id="conversation-friend-name">-</span>
              <span class="friend-status" id="conversation-friend-status">-</span>
            </div>
          </div>
          <div class="conversation-messages" id="conversation-messages">
            <!-- Messages will be rendered here -->
          </div>
          <div class="conversation-input">
            <input type="text" id="chat-message-input" placeholder="Digite uma mensagem..." maxlength="500">
            <button class="send-btn" id="chat-send-btn">➤</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.container);

    // Setup event listeners
    this.setupEventListeners();
  }

  /**
   * Setup UI event listeners
   */
  private setupEventListeners(): void {
    // Toggle expand/minimize
    document.getElementById('chat-expand-btn')?.addEventListener('click', () => {
      this.toggleMinimize();
    });

    // Setup drag listeners
    this.setupDragListeners();

    // Mobile mode: Back button
    document.getElementById('chat-back-btn')?.addEventListener('click', () => {
      this.closeConversationMobile();
    });

    // Mobile mode: Send message
    document.getElementById('chat-send-btn')?.addEventListener('click', () => {
      this.sendMessageMobile();
    });

    document.getElementById('chat-message-input')?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.sendMessageMobile();
      }
    });
  }

  // ============================================================================
  // PRIVATE - CHATSERVICE EVENT HANDLERS
  // ============================================================================

  private subscribeToEvents(): void {
    const chatService = ChatService.getInstance();
    chatService.on('connected', this.boundOnConnected);
    chatService.on('friendsUpdated', this.boundOnFriendsUpdated);
    chatService.on('conversationsUpdated', this.boundOnConversationsUpdated);
    chatService.on('chatReceived', this.boundOnChatReceived);
    chatService.on('messagesLoaded', this.boundOnMessagesLoaded);
    chatService.on('disconnected', this.boundOnDisconnected);
  }

  private unsubscribeFromEvents(): void {
    const chatService = ChatService.getInstance();
    chatService.off('connected', this.boundOnConnected);
    chatService.off('friendsUpdated', this.boundOnFriendsUpdated);
    chatService.off('conversationsUpdated', this.boundOnConversationsUpdated);
    chatService.off('chatReceived', this.boundOnChatReceived);
    chatService.off('messagesLoaded', this.boundOnMessagesLoaded);
    chatService.off('disconnected', this.boundOnDisconnected);
  }

  private onConnected(data: any): void {
    console.log(`💬 [2025-12-19] PrivateChat: Connected event received`);
    if (data.friends) {
      this.friends = data.friends;
      this.updateFriendsList();
    }
    // Request conversations
    ChatService.getInstance().requestConversations();
  }

  private onFriendsUpdated(data: any): void {
    this.friends = data.friends || [];
    this.updateFriendsList();
  }

  private onConversationsUpdated(data: any): void {
    this.conversations = data.conversations || [];
    this.updateFriendsList();
    this.updateUnreadBadge();
  }

  private onChatReceived(data: any): void {
    // Handle notification for non-active conversations
    const hasOpenWindow = ChatWindowManager.getInstance().getWindow(data.fromNetcartasId) !== null;

    // In mobile mode, check if viewing this conversation
    const isViewingInMobile = this.isMobileMode && this.currentConversation === data.fromNetcartasId && !this.isMinimized;

    // If message is for current mobile conversation, add it
    if (this.isMobileMode && this.currentConversation === data.fromNetcartasId) {
      const newMsg: ChatMessage = {
        fromNetcartasId: data.fromNetcartasId,
        toNetcartasId: ChatService.getInstance().getMyNetcartasId()!,
        message: data.message,
        createdAt: data.createdAt || Date.now(),
        isSystem: data.isSystem
      };
      this.messages.push(newMsg);
      this.renderMessagesMobile();

      if (isViewingInMobile) {
        ChatService.getInstance().markAsRead(data.fromNetcartasId);
      }
    }

    // If no window is open and not viewing in mobile, show notification
    if (!hasOpenWindow && !isViewingInMobile) {
      // Auto-open behavior
      const friend = this.friends.find(f => f.netcartasId === data.fromNetcartasId);
      const userMinimized = this.hasUserMinimizedConversation(data.fromNetcartasId);

      if (!userMinimized && !data.isSystem) {
        // Auto-open chat
        this.openChatWith(data.fromNetcartasId, friend?.nickname || 'Amigo', data.isAI);
      } else if (!data.isSystem) {
        // Just play sound and flash
        ChatService.getInstance().playNotificationSound();
        this.showNotification();
      }

      // Handle system messages (like "Amizade aceita")
      if (data.isSystem) {
        this.openChatWith(data.fromNetcartasId, friend?.nickname || 'Amigo', data.isAI);
      }
    }

    // Update unread badge
    this.updateUnreadBadge();
    this.updateFriendsList();
  }

  private onMessagesLoaded(data: any): void {
    // Only handle in mobile mode
    if (this.isMobileMode && data.friendNetcartasId === this.currentConversation) {
      this.messages = data.messages || [];
      this.renderMessagesMobile();
    }
  }

  private onDisconnected(_data: any): void {
    console.log(`💬 [2025-12-19] PrivateChat: Disconnected`);
  }

  // ============================================================================
  // PRIVATE - FRIENDS LIST
  // ============================================================================

  /**
   * Update friends list UI
   */
  private updateFriendsList(): void {
    const listEl = document.getElementById('chat-friends-list');
    if (!listEl) return;

    if (this.friends.length === 0) {
      listEl.innerHTML = '<div class="empty-friends">Nenhum amigo ainda</div>';
      return;
    }

    // Sort friends: online first, then by last message
    const sortedFriends = [...this.friends].sort((a, b) => {
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      return (b.lastMessageAt || 0) - (a.lastMessageAt || 0);
    });

    listEl.innerHTML = sortedFriends.map(friend => {
      const conv = this.conversations.find(c => c.friendNetcartasId === friend.netcartasId);
      const unread = conv?.unreadCount || 0;
      const lastMsg = conv?.lastMessage || '';
      const statusClass = friend.isOnline ? 'online' : 'offline';
      const aiClass = friend.isAI ? 'ai-friend' : '';

      // DATE: 2025-12-21 | Avatar: use image for AI companions, initials for humans
      // DATE: 2026-01-23 | Fix: Remove "_ia" suffix from nickname to match avatar filename
      // Avatar files are named without the _ia suffix (e.g., "dubi-avatar.jpg" not "dubi_ia-avatar.jpg")
      const avatarBaseName = friend.nickname.toLowerCase().replace(/_ia$/, '');
      const avatarContent = friend.isAI
        ? `<img src="https://domino.netcartas.com.br/assets/avatars/${avatarBaseName}-avatar.jpg" alt="${this.escapeHtml(friend.nickname)}" class="avatar-img" />`
        : `<span class="avatar-initial">${friend.nickname.charAt(0).toUpperCase()}</span>`;

      return `
        <div class="friend-item ${aiClass}" data-netcartas-id="${friend.netcartasId}" data-is-ai="${friend.isAI}" data-nickname="${this.escapeHtml(friend.nickname)}">
          <div class="friend-avatar">
            ${avatarContent}
            <span class="status-dot ${statusClass}"></span>
          </div>
          <div class="friend-info">
            <div class="friend-name-row">
              <span class="friend-nickname">${this.escapeHtml(friend.nickname)}</span>
              ${friend.isAI ? '<span class="ai-tag">IA</span>' : ''}
            </div>
            <div class="friend-last-msg">${lastMsg ? this.truncate(lastMsg, 30) : 'Iniciar conversa'}</div>
          </div>
          ${unread > 0 ? `<span class="unread-count">${unread}</span>` : ''}
        </div>
      `;
    }).join('');

    // Add click handlers
    listEl.querySelectorAll('.friend-item').forEach(item => {
      item.addEventListener('click', () => {
        const netcartasId = parseInt(item.getAttribute('data-netcartas-id') || '0');
        const isAI = item.getAttribute('data-is-ai') === 'true';
        const nickname = item.getAttribute('data-nickname') || '';
        this.handleFriendClick(netcartasId, nickname, isAI);
      });
    });
  }

  /**
   * Handle click on a friend in the list
   */
  private handleFriendClick(friendId: number, nickname: string, isAI: boolean): void {
    if (this.isMobileMode) {
      // Mobile: use single-panel navigation
      this.openConversationMobile(friendId, nickname, isAI);
    } else {
      // Desktop: open a ConversationWindow
      ChatWindowManager.getInstance().openWindow(friendId, nickname, isAI);
    }
  }

  /**
   * Ensure a friend exists in the list
   */
  private ensureFriendExists(netcartasId: number, nickname: string, isAI: boolean): void {
    const existing = this.friends.find(f => f.netcartasId === netcartasId);
    if (!existing) {
      const newFriend: Friend = {
        netcartasId,
        nickname,
        isAI,
        isOnline: true
      };
      this.friends.push(newFriend);
      this.updateFriendsList();
    }
  }

  // ============================================================================
  // PRIVATE - MOBILE MODE (Single-Panel Navigation)
  // ============================================================================

  /**
   * Open conversation in mobile mode (single-panel)
   */
  private openConversationMobile(friendId: number, nickname: string, isAI: boolean): void {
    console.log(`💬 [2025-12-19] PrivateChat: Opening mobile conversation with ${nickname}`);

    this.currentConversation = friendId;
    this.messages = [];

    // Clear minimized flag
    this.clearConversationMinimized(friendId);

    // Update header
    const nameEl = document.getElementById('conversation-friend-name');
    const statusEl = document.getElementById('conversation-friend-status');
    const friend = this.friends.find(f => f.netcartasId === friendId);
    if (nameEl) nameEl.textContent = nickname;
    if (statusEl) statusEl.textContent = (friend?.isOnline || isAI) ? 'Online' : 'Offline';

    // Show conversation panel
    document.getElementById('chat-friends-panel')?.classList.add('hidden');
    document.getElementById('chat-conversation-panel')?.classList.remove('hidden');

    // Request messages
    ChatService.getInstance().requestMessages(friendId);
    ChatService.getInstance().markAsRead(friendId);

    // Focus input
    document.getElementById('chat-message-input')?.focus();
  }

  /**
   * Close conversation and go back to friends list (mobile)
   */
  private closeConversationMobile(): void {
    this.currentConversation = null;
    this.messages = [];

    document.getElementById('chat-friends-panel')?.classList.remove('hidden');
    document.getElementById('chat-conversation-panel')?.classList.add('hidden');

    // Refresh conversations
    ChatService.getInstance().requestConversations();
  }

  /**
   * Send message in mobile mode
   */
  private sendMessageMobile(): void {
    const input = document.getElementById('chat-message-input') as HTMLInputElement;
    const message = input?.value.trim();

    if (!message || !this.currentConversation) return;

    const friend = this.friends.find(f => f.netcartasId === this.currentConversation);
    if (!friend) return;

    // Send via ChatService
    ChatService.getInstance().sendMessage(this.currentConversation, message, friend.isAI);

    // Optimistic update
    const newMsg: ChatMessage = {
      fromNetcartasId: ChatService.getInstance().getMyNetcartasId()!,
      toNetcartasId: this.currentConversation,
      message,
      createdAt: Date.now()
    };
    this.messages.push(newMsg);
    this.renderMessagesMobile();

    input.value = '';
  }

  /**
   * Render messages in mobile mode
   */
  private renderMessagesMobile(): void {
    const container = document.getElementById('conversation-messages');
    if (!container) return;

    const myId = ChatService.getInstance().getMyNetcartasId();

    container.innerHTML = this.messages.map(msg => {
      const time = new Date(msg.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

      if (msg.isSystem) {
        return `
          <div class="chat-message system">
            <div class="system-message-bubble">
              <span class="message-text">${this.escapeHtml(msg.message)}</span>
            </div>
          </div>
        `;
      }

      const isMine = msg.fromNetcartasId === myId;
      return `
        <div class="chat-message ${isMine ? 'mine' : 'theirs'}">
          <div class="message-bubble">
            <span class="message-text">${this.escapeHtml(msg.message)}</span>
            <span class="message-time">${time}</span>
          </div>
        </div>
      `;
    }).join('');

    container.scrollTop = container.scrollHeight;
  }

  // ============================================================================
  // PRIVATE - MINIMIZE/EXPAND
  // ============================================================================

  /**
   * Toggle minimized state
   */
  private toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;

    const body = document.getElementById('private-chat-body');
    const btn = document.getElementById('chat-expand-btn');

    if (this.isMinimized) {
      this.container?.classList.add('minimized');
      body?.classList.add('hidden');
      if (btn) btn.textContent = '▲';

      // Track minimization for mobile conversations
      if (this.isMobileMode && this.currentConversation !== null) {
        this.setConversationMinimized(this.currentConversation);
      }
    } else {
      this.container?.classList.remove('minimized');
      body?.classList.remove('hidden');
      if (btn) btn.textContent = '▼';

      // Request conversations when opening
      ChatService.getInstance().requestConversations();
    }
  }

  /**
   * Expand the hub (show friends list)
   */
  private expand(): void {
    if (this.isMinimized) {
      this.isMinimized = false;
      this.container?.classList.remove('minimized');
      const body = document.getElementById('private-chat-body');
      const btn = document.getElementById('chat-expand-btn');
      body?.classList.remove('hidden');
      if (btn) btn.textContent = '▼';
    }
  }

  // ============================================================================
  // PRIVATE - DRAG FUNCTIONALITY
  // ============================================================================

  private setupDragListeners(): void {
    const header = document.getElementById('private-chat-header');
    if (!header || !this.container) return;

    header.addEventListener('mousedown', (e) => this.startDrag(e));
    document.addEventListener('mousemove', (e) => this.onDrag(e));
    document.addEventListener('mouseup', () => this.stopDrag());

    header.addEventListener('touchstart', (e) => this.startDragTouch(e), { passive: false });
    document.addEventListener('touchmove', (e) => this.onDragTouch(e), { passive: false });
    document.addEventListener('touchend', () => this.stopDrag());
  }

  private startDrag(e: MouseEvent): void {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return;

    this.isDragging = true;
    this.hasDragMoved = false;
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;

    if (this.container) {
      const rect = this.container.getBoundingClientRect();
      this.containerStartX = rect.left;
      this.containerStartY = rect.top;
      this.container.classList.add('dragging');
    }

    e.preventDefault();
  }

  private startDragTouch(e: TouchEvent): void {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return;
    if (e.touches.length !== 1) return;

    this.isDragging = true;
    this.hasDragMoved = false;
    this.dragStartX = e.touches[0].clientX;
    this.dragStartY = e.touches[0].clientY;

    if (this.container) {
      const rect = this.container.getBoundingClientRect();
      this.containerStartX = rect.left;
      this.containerStartY = rect.top;
      this.container.classList.add('dragging');
    }

    e.preventDefault();
  }

  private onDrag(e: MouseEvent): void {
    if (!this.isDragging || !this.container) return;

    const deltaX = e.clientX - this.dragStartX;
    const deltaY = e.clientY - this.dragStartY;

    const DRAG_THRESHOLD = 5;
    if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
      this.hasDragMoved = true;
    }

    this.updateContainerPosition(deltaX, deltaY);
  }

  private onDragTouch(e: TouchEvent): void {
    if (!this.isDragging || !this.container || e.touches.length !== 1) return;

    const deltaX = e.touches[0].clientX - this.dragStartX;
    const deltaY = e.touches[0].clientY - this.dragStartY;

    const DRAG_THRESHOLD = 5;
    if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
      this.hasDragMoved = true;
    }

    this.updateContainerPosition(deltaX, deltaY);
    e.preventDefault();
  }

  private updateContainerPosition(deltaX: number, deltaY: number): void {
    if (!this.container) return;

    let newLeft = this.containerStartX + deltaX;
    let newTop = this.containerStartY + deltaY;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const containerRect = this.container.getBoundingClientRect();

    const minVisible = 100;
    newLeft = Math.max(-containerRect.width + minVisible, Math.min(newLeft, viewportWidth - minVisible));
    newTop = Math.max(0, Math.min(newTop, viewportHeight - 48));

    this.container.style.left = `${newLeft}px`;
    this.container.style.top = `${newTop}px`;
    this.container.style.right = 'auto';
    this.container.style.bottom = 'auto';
  }

  private stopDrag(): void {
    if (this.isDragging && this.container) {
      const wasActualDrag = this.hasDragMoved;

      this.isDragging = false;
      this.container.classList.remove('dragging');

      if (!wasActualDrag) {
        this.toggleMinimize();
      }
    }
  }

  // ============================================================================
  // PRIVATE - NOTIFICATIONS
  // ============================================================================

  /**
   * Update unread badge
   */
  private updateUnreadBadge(): void {
    const badge = document.getElementById('chat-unread-badge');
    if (!badge) return;

    const totalUnread = this.conversations.reduce((sum, c) => sum + c.unreadCount, 0);

    if (totalUnread > 0) {
      badge.textContent = totalUnread > 99 ? '99+' : String(totalUnread);
      badge.classList.remove('hidden');
    } else {
      badge.classList.add('hidden');
    }
  }

  /**
   * Show visual notification (flashing header)
   */
  private showNotification(): void {
    if (!this.isMinimized) return;

    this.container?.classList.add('has-notification');
    setTimeout(() => {
      this.container?.classList.remove('has-notification');
    }, 2000);
  }

  // ============================================================================
  // PRIVATE - LOCALSTORAGE
  // ============================================================================

  private readonly MINIMIZED_KEY_PREFIX = 'domino_chat_minimized_';

  private hasUserMinimizedConversation(netcartasId: number): boolean {
    try {
      const key = `${this.MINIMIZED_KEY_PREFIX}${netcartasId}`;
      return localStorage.getItem(key) === 'true';
    } catch {
      return false;
    }
  }

  private setConversationMinimized(netcartasId: number): void {
    try {
      const key = `${this.MINIMIZED_KEY_PREFIX}${netcartasId}`;
      localStorage.setItem(key, 'true');
    } catch {
      // Ignore
    }
  }

  private clearConversationMinimized(netcartasId: number): void {
    try {
      const key = `${this.MINIMIZED_KEY_PREFIX}${netcartasId}`;
      localStorage.removeItem(key);
    } catch {
      // Ignore
    }
  }

  // ============================================================================
  // PRIVATE - HELPERS
  // ============================================================================

  private truncate(str: string, maxLen: number): string {
    return str.length > maxLen ? str.substring(0, maxLen) + '...' : str;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
